import { useState } from "react"
import { XClose } from "@untitledui/icons"
import { toast } from "sonner"
import { useSendPromotion } from "@/lib/api/use-campaigns"

// Bulk outreach to a hand-picked audience — shared by the Customers book and
// the Inbox ("message who you're looking at"). The server enforces the
// consent + no-contact guardrails regardless of the ids sent; this dialog
// pre-filters to reachable people so the count on the button is honest.

export interface PromoRecipient {
  id: number
  phone: string | null
  email: string | null
}

// Quiet hours (8 AM – 8 PM): outside the window the server holds the batch
// for the next morning. Mirrors the mock's clamp so the dialog can say so
// up front instead of surprising after send.
const outsideQuietHours = () => {
  const h = new Date().getHours()
  return h < 8 || h >= 20
}

const PROMO_TEMPLATES: { label: string; body: string }[] = [
  {
    label: "Win-back",
    body: "It's been a while! Mention this text for 10% off any bodywork this month.",
  },
  {
    label: "Review ask",
    body: "Thanks for trusting us with your repair — if you were happy with the work, a quick review would mean a lot 🙏",
  },
  {
    label: "Free check",
    body: "Free 15-minute damage check with any visit this month — no appointment needed.",
  },
]

export function PromoDialog({
  open,
  onClose,
  recipients,
  audienceLabel,
}: {
  open: boolean
  onClose: () => void
  recipients: PromoRecipient[]
  audienceLabel: string
}) {
  const send = useSendPromotion()
  const [body, setBody] = useState("")
  const reachable = recipients.filter((c) => c.phone || c.email)

  if (!open) return null

  const submit = () => {
    const text = body.trim()
    if (!text || reachable.length === 0 || send.isPending) return
    send.mutate(
      { customerIds: reachable.map((c) => c.id), body: text },
      {
        onSuccess: (res) => {
          if (res.heldForMorning > 0) {
            // Quiet hours — nothing leaves tonight; the send is queued.
            toast.success(
              `Queued for 8:00 AM — ${res.sent} customer${res.sent === 1 ? "" : "s"} (quiet hours)`,
            )
          } else {
            toast.success(
              `Promotion sent to ${res.sent} customer${res.sent === 1 ? "" : "s"}`,
            )
          }
          if (res.skippedConsent > 0) {
            toast(`${res.skippedConsent} skipped — opted out or handled offline`)
          }
          setBody("")
          onClose()
        },
        onError: () => toast.error("Couldn't send the promotion."),
      },
    )
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0 bg-zinc-900/40" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Send a promotion"
        className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl p-5"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[16px] font-bold text-zinc-900">Send a promotion</p>
            <p className="text-[12.5px] text-zinc-500 mt-0.5 leading-snug">
              Goes to the <span className="font-semibold">{audienceLabel}</span> view
              you're looking at — {reachable.length} reachable customer
              {reachable.length === 1 ? "" : "s"}. Anyone opted out or handled
              offline is skipped automatically.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="size-8 grid place-items-center rounded-lg text-zinc-400 hover:text-zinc-900 hover:bg-zinc-50 shrink-0"
          >
            <XClose className="size-4" aria-hidden />
          </button>
        </div>

        <p className="mt-4 text-[10.5px] font-bold uppercase tracking-[0.12em] text-zinc-400">
          Start from a template
        </p>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {PROMO_TEMPLATES.map((t) => (
            <button
              key={t.label}
              type="button"
              onClick={() => setBody(t.body)}
              className="h-8 px-3 rounded-lg border border-zinc-200 bg-white text-[12px] font-semibold text-zinc-600 hover:border-zinc-300 hover:text-zinc-900 transition-colors"
            >
              {t.label}
            </button>
          ))}
        </div>

        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          aria-label="Promotion message"
          placeholder="Write the message your customers will get…"
          className="mt-3 w-full resize-none rounded-xl border border-zinc-200 bg-white px-3 py-2 text-[13.5px] text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-300"
        />
        <p className="mt-1.5 text-[11.5px] text-zinc-400 leading-snug">
          We add &ldquo;Reply STOP to opt out.&rdquo; automatically.
        </p>

        {outsideQuietHours() && (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/70 px-3 py-2 text-[12.5px] text-amber-900 leading-snug">
            It's outside quiet hours (8 AM – 8 PM) — this will be queued and go
            out at 8:00 AM.
          </div>
        )}

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 rounded-xl text-[13px] font-semibold text-zinc-600 hover:bg-zinc-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!body.trim() || reachable.length === 0 || send.isPending}
            className="h-10 px-4 rounded-xl bg-zinc-900 text-white text-[13px] font-semibold hover:bg-black transition-colors disabled:opacity-40"
          >
            {send.isPending ? "Sending…" : `Send to ${reachable.length}`}
          </button>
        </div>
      </div>
    </div>
  )
}
